import { formatMoney } from '@theme/money';
import { fetchJSON, routeUrl } from '@theme/utils';

/**
 * @typedef {object} ProductVariant
 * @property {number} id
 * @property {number} price - Cents.
 * @property {number} compare - Compare-at price in cents (0 when none).
 * @property {boolean} available
 * @property {string} title
 * @property {number | null} ppg - Price per gram in cents, when known.
 * @property {number | null} media - Id of the variant's featured media.
 */

/**
 * @typedef {object} ProductData
 * @property {string} moneyFormat
 * @property {number} threshold - Free shipping threshold in cents (0 = disabled).
 * @property {number} cartTotal - Cart total in cents.
 * @property {ProductVariant[]} variants
 * @property {Record<string, string>} strings - Translated templates (`{price}`, `{amount}`, `{index}`, `{total}`).
 */

/**
 * Free shipping progress for the product page box (mockup `renderVals`: `shippingMsg`, `shippingPct`,
 * `shipGoal*`). An empty cart measures the line being chosen; otherwise the cart total.
 *
 * @param {number} threshold - Free shipping threshold, cents (> 0).
 * @param {number} cartTotal - Current cart total, cents.
 * @param {number} line - Selected format price × quantity, cents.
 * @returns {{ state: 'cart-free' | 'line-free' | 'remaining', remaining: number, pct: number, free: boolean }}
 */
export function shippingProgress(threshold, cartTotal, line) {
  const base = cartTotal > 0 ? cartTotal : line;
  const pct = Math.min(100, Math.max(4, Math.round((base / threshold) * 100)));
  if (cartTotal >= threshold) return { state: 'cart-free', remaining: 0, pct, free: true };
  if (cartTotal === 0 && line >= threshold) return { state: 'line-free', remaining: 0, pct, free: true };
  return { state: 'remaining', remaining: threshold - base, pct, free: false };
}

/**
 * Keeps a quantity between 1 and `max` (whole numbers only).
 *
 * @param {number} value
 * @param {number} [max]
 */
export function clampQuantity(value, max = 99) {
  const n = Math.floor(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(1, n)) : 1;
}

/**
 * Fills `{name}` placeholders of a translated template.
 *
 * @param {string | undefined} template
 * @param {Record<string, string | number>} values
 */
export function fill(template, values) {
  return (template ?? '').replace(/\{(\w+)\}/g, (match, /** @type {string} */ key) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * Product page behaviour (sections/main-product.liquid), all progressive enhancement over the native form:
 * - gallery: arrows, thumbnails and « 1 / 5 » counter (one photo at a time on mobile);
 * - format radios → price, price per gram, button label, `?variant=` URL and variant photo, and `product:variant`
 *   on the document (assets/stock-alert.js);
 * - quantity stepper, free-shipping progress, « added » feedback;
 * - `html.gs-pastcta` while the main button is out of view (shows the mobile buy bar).
 *
 * Data: `<script type="application/json" data-product-json>` (see {@link ProductData}).
 */
export class ProductPage extends HTMLElement {
  /** @type {ProductData | undefined} */
  #data;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedTimer;
  #added = false;
  #index = 0;

  // Listeners go on the host (delegation): children may not be parsed yet when connectedCallback runs.
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('product-form:added', this.onAdded);
    document.addEventListener('cart:updated', this.onCartUpdated);
    // Module scripts run after parsing, so the button exists here; the guard covers editor re-renders.
    requestAnimationFrame(() => this.observeCta());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('product-form:added', this.onAdded);
    document.removeEventListener('cart:updated', this.onCartUpdated);
    this.#observer?.disconnect();
    clearTimeout(this.#addedTimer);
    document.documentElement.classList.remove('gs-pastcta');
  }

  /** @returns {ProductData} */
  get data() {
    if (!this.#data) {
      const json = this.querySelector('script[data-product-json]')?.textContent;
      this.#data = {
        moneyFormat: '{{amount_with_comma_separator}} €',
        threshold: 0,
        cartTotal: 0,
        variants: [],
        strings: {},
      };
      try {
        if (json) this.#data = { ...this.#data, ...JSON.parse(json) };
      } catch {
        // Keep the defaults: the page still works as a plain form.
      }
    }
    return /** @type {ProductData} */ (this.#data);
  }

  /** @returns {ProductVariant | undefined} */
  get variant() {
    const input = this.querySelector('input[name="id"]:checked, input[type="hidden"][name="id"]');
    const id = Number(input instanceof HTMLInputElement ? input.value : NaN);
    return this.data.variants.find((v) => v.id === id) ?? this.data.variants[0];
  }

  get quantity() {
    const input = this.querySelector('[data-qty]');
    return input instanceof HTMLInputElement ? clampQuantity(Number(input.value)) : 1;
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(cents, this.data.moneyFormat);
  }

  /** @param {Event} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const step = target?.closest('[data-gal-step]');
    if (step) return this.showShot(this.#index + Number(step.getAttribute('data-gal-step')));
    const thumb = target?.closest('[data-gal-thumb]');
    if (thumb) return this.showShot(Number(thumb.getAttribute('data-gal-thumb')));
    const qty = target?.closest('[data-qty-step]');
    const input = this.querySelector('[data-qty]');
    if (qty && input instanceof HTMLInputElement) {
      input.value = String(clampQuantity(this.quantity + Number(qty.getAttribute('data-qty-step'))));
      this.render();
    }
  };

  /** @param {Event} event */
  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'id') {
      const variant = this.variant;
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url);
        this.showMedia(variant.media);
        document.dispatchEvent(new CustomEvent('product:variant', { detail: { variant } }));
      }
    }
    if (target.matches('[data-qty]')) target.value = String(this.quantity);
    this.render();
  };

  /** @param {Event} event */
  onInput = (event) => {
    if (event.target instanceof HTMLElement && event.target.matches('[data-qty]')) this.render();
  };

  onAdded = () => {
    this.#added = true;
    this.render();
    clearTimeout(this.#addedTimer);
    this.#addedTimer = setTimeout(() => {
      this.#added = false;
      this.render();
    }, 1600);
  };

  /** @param {DocumentEventMap['cart:updated']} event */
  onCartUpdated = async (event) => {
    if (!this.data.threshold) return;
    const total = event.detail?.total;
    if (typeof total === 'number' && Number.isFinite(total)) {
      this.data.cartTotal = total;
      this.render();
      return;
    }
    try {
      /** @type {{ total_price: number }} */
      const cart = await fetchJSON(routeUrl('cart.js'));
      this.data.cartTotal = cart.total_price;
      this.render();
    } catch {
      // Keep the previous total: the box is only informative.
    }
  };

  /** @param {number} index - Wraps around. */
  showShot(index) {
    const shots = [...this.querySelectorAll('.gs-shot')];
    if (!shots.length) return;
    this.#index = (index + shots.length) % shots.length;
    shots.forEach((shot, i) => shot.classList.toggle('is-on', i === this.#index));
    for (const thumb of this.querySelectorAll('[data-gal-thumb]'))
      thumb.setAttribute('aria-pressed', String(Number(thumb.getAttribute('data-gal-thumb')) === this.#index));
    const count = this.querySelector('[data-gal-count]');
    if (count)
      count.textContent = fill(this.data.strings.count || '{index} / {total}', {
        index: this.#index + 1,
        total: shots.length,
      });
  }

  /** @param {number | null} mediaId */
  showMedia(mediaId) {
    if (!mediaId) return;
    const shots = [...this.querySelectorAll('.gs-shot')];
    const index = shots.findIndex((s) => s.getAttribute('data-media-id') === String(mediaId));
    if (index >= 0) this.showShot(index);
  }

  /** Re-renders every price-dependent text from the selected format and quantity. */
  render() {
    const variant = this.variant;
    if (!variant) return;
    const { strings } = this.data;
    const qty = this.quantity;
    const line = variant.price * qty;
    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      for (const el of this.querySelectorAll(selector)) el.textContent = text;
    };

    set('[data-price]', this.money(variant.price));
    const compare = this.querySelector('[data-compare]');
    if (compare instanceof HTMLElement) {
      compare.hidden = !(variant.compare > variant.price);
      set('[data-compare-value]', compare.hidden ? '' : this.money(variant.compare));
    }
    const ppg = this.querySelector('[data-ppg]');
    if (ppg instanceof HTMLElement) {
      ppg.hidden = !variant.ppg;
      ppg.textContent = variant.ppg ? fill(strings.perGram, { price: this.money(variant.ppg) }) : '';
    }

    let long = fill(strings.addPrice, { price: this.money(line) });
    let short = strings.add ?? '';
    if (!variant.available) {
      long = strings.soldOutSoon ?? '';
      short = strings.soldOut ?? '';
    } else if (this.#added) [long, short] = [strings.added ?? '', strings.addedShort ?? ''];
    set('[data-cta-long]', long);
    set('[data-cta-short]', short);
    for (const button of this.querySelectorAll('[data-cta], [data-mb-cta]')) {
      if (!(button instanceof HTMLButtonElement) || button.hasAttribute('aria-busy')) continue;
      button.disabled = !variant.available;
      button.classList.toggle('is-out', !variant.available);
    }
    const mobileCta = this.querySelector('[data-mb-cta]');
    if (mobileCta) mobileCta.textContent = variant.available ? short : (strings.soldOut ?? '');
    set('[data-mb-price]', this.money(line));
    set('[data-mb-variant]', `${variant.title} × ${qty}`);

    const { threshold, cartTotal } = this.data;
    if (threshold > 0) {
      const ship = shippingProgress(threshold, cartTotal, line);
      const message = {
        'cart-free': strings.cartFree,
        'line-free': strings.lineFree,
        remaining: fill(strings.remaining, { amount: this.money(ship.remaining) }),
      }[ship.state];
      set('[data-ship-msg]', message ?? '');
      this.querySelector('[data-ship-bar]')?.setAttribute('style', `--w: ${ship.pct}%;`);
      this.querySelector('[data-ship-goal]')?.classList.toggle('is-free', ship.free);
    }
  }

  /**
   * The mobile buy bar shows whenever the main button is out of view: below the gallery on arrival (price and
   * « Ajouter au panier » in the first screen of a phone), or once scrolled past.
   */
  observeCta() {
    const cta = this.querySelector('[data-cta]');
    if (!cta || this.#observer) return;
    if (!('IntersectionObserver' in window)) {
      document.documentElement.classList.add('gs-pastcta');
      return;
    }
    this.#observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      document.documentElement.classList.toggle('gs-pastcta', !entry.isIntersecting);
    });
    this.#observer.observe(cta);
  }
}

if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);
